import React, { useEffect, useState } from 'react';
import { Loader2, Power, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  getAppVersionFieldErrors,
  useToggleMaintenanceMutation,
} from '@/client/api/app-version/app-version.mutation';
import { AppVersionFieldErrors, MaintenanceConfig } from '@/client/types/app-version';
import { cn } from '@/lib/utils';
import { APP_VERSION_DEFAULTS, blankIfDefault, effectiveValue } from '../app-version-form';
import { FormField } from './FormField';

interface MaintenanceCardProps {
  maintenance: MaintenanceConfig;
}

export const MaintenanceCard: React.FC<MaintenanceCardProps> = ({ maintenance }) => {
  const savedTitle = blankIfDefault('maintenance', 'title', maintenance.title);
  const savedMessage = blankIfDefault('maintenance', 'message', maintenance.message);
  const [title, setTitle] = useState(savedTitle);
  const [message, setMessage] = useState(savedMessage);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [errors, setErrors] = useState<AppVersionFieldErrors>({});
  const mutation = useToggleMaintenanceMutation();

  const copyDirty = title !== savedTitle || message !== savedMessage;
  // Empty inputs fall back to the default copy, which is what phones will show.
  const shownTitle = effectiveValue('maintenance', 'title', title);
  const shownMessage = effectiveValue('maintenance', 'message', message);
  const enabled = maintenance.enabled;

  useEffect(() => {
    if (!copyDirty) {
      setTitle(savedTitle);
      setMessage(savedMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedTitle, savedMessage]);

  const submit = (nextEnabled: boolean, successText: string) => {
    mutation.mutate(
      { enabled: nextEnabled, title: shownTitle, message: shownMessage },
      {
        onSuccess: (next) => {
          setTitle(blankIfDefault('maintenance', 'title', next.title));
          setMessage(blankIfDefault('maintenance', 'message', next.message));
          setErrors({});
          setConfirmOpen(false);
          toast.success(successText);
        },
        onError: (error) => {
          setErrors(getAppVersionFieldErrors(error));
          setConfirmOpen(false);
          toast.error(getApiErrorMessage(error, 'Could not update maintenance mode'));
        },
      },
    );
  };

  return (
    <Card className={cn(enabled && 'border-red-300 ring-1 ring-red-200')}>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Wrench className="h-5 w-5 text-gray-500" />
            Maintenance mode
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-xs font-medium',
                enabled ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700',
              )}
            >
              {enabled ? 'On: apps are blocked' : 'Off'}
            </span>
          </CardTitle>
          <CardDescription>
            While on, every iOS and Android user sees the maintenance screen on launch and can't go
            further. The website is not affected.
          </CardDescription>
        </div>
        <PermissionButton
          code={PERMISSIONS.system.settings}
          variant={enabled ? 'outline' : 'destructive'}
          onClick={() => setConfirmOpen(true)}
          disabled={mutation.isPending}
        >
          <Power className="mr-2 h-4 w-4" />
          {enabled ? 'Turn off' : 'Turn on'}
        </PermissionButton>
      </CardHeader>
      <CardContent className="space-y-3">
        <FormField
          id="app-version-maintenance-title"
          label="Title"
          value={title}
          onChange={setTitle}
          placeholder={APP_VERSION_DEFAULTS.maintenance.title}
          error={errors['maintenance.title']}
        />
        <FormField
          id="app-version-maintenance-message"
          label="Message"
          hint="Say when you expect to be back, e.g. “We'll be back at 14:00 GMT.”"
          value={message}
          onChange={setMessage}
          placeholder={APP_VERSION_DEFAULTS.maintenance.message}
          error={errors['maintenance.message']}
          multiline
        />
        <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
          <Button
            variant="outline"
            onClick={() => {
              setTitle(savedTitle);
              setMessage(savedMessage);
            }}
            disabled={!copyDirty || mutation.isPending}
          >
            Reset
          </Button>
          <PermissionButton
            code={PERMISSIONS.system.settings}
            onClick={() => submit(enabled, 'Maintenance copy saved')}
            disabled={!copyDirty || mutation.isPending}
          >
            {mutation.isPending && !confirmOpen && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save copy
          </PermissionButton>
        </div>
      </CardContent>

      <AlertDialog open={confirmOpen} onOpenChange={(open) => !mutation.isPending && setConfirmOpen(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {enabled ? 'Turn maintenance mode off?' : 'Turn maintenance mode on?'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              {enabled ? (
                <p>Phones will let users back into the app on their next launch.</p>
              ) : (
                <div className="space-y-3">
                  <p>
                    Every mobile user will be blocked on their next launch and shown this screen
                    until you turn it off:
                  </p>
                  <div className="rounded-md border bg-gray-50 p-3 text-left">
                    <p className="font-semibold text-gray-900">{shownTitle}</p>
                    <p className="mt-1 whitespace-pre-line text-gray-600">{shownMessage}</p>
                  </div>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={mutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              // Keep the dialog open until the request settles.
              onClick={(e) => {
                e.preventDefault();
                submit(!enabled, enabled ? 'Maintenance mode is off' : 'Maintenance mode is on');
              }}
              disabled={mutation.isPending}
              className={cn(!enabled && 'bg-red-600 hover:bg-red-700')}
            >
              {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {enabled ? 'Turn off' : 'Turn on maintenance'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default MaintenanceCard;
